import { useState, useMemo, useEffect } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { patientService, PatientSearchParams, extractPatients } from '../services/patientService'
import { Patient } from '../types/patient'
import { PatientSearchFilters, PatientFilters } from '../components/PatientSearchFilters'
import { PatientBulkActions } from '../components/PatientBulkActions'
import { exportPatientsToCSV, exportPatientsToJSON } from '../utils/exportUtils'
import { formatDate } from '../utils/locale'
import './Patients.css'

export function Patients() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<PatientFilters>({ search: '' })
  const [searchParams, setSearchParams] = useState<PatientSearchParams | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedPatients, setSelectedPatients] = useState<string[]>([])
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [sortBy, setSortBy] = useState<'patientNumber' | 'name' | 'dateOfBirth'>('name')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Convert filters to structured search parameters
  const buildSearchParams = useMemo((): PatientSearchParams | null => {
    const hasSearchTerm = filters.search && filters.search.trim().length > 0
    const hasOtherFilters = filters.gender || filters.status || filters.dateOfBirthFrom || 
                            filters.dateOfBirthTo || filters.hasAllergies !== undefined || 
                            filters.hasInsurance !== undefined
    
    if (!hasSearchTerm && !hasOtherFilters) {
      return null
    }
    
    const params: PatientSearchParams = {}
    if (hasSearchTerm) {
      params.search = filters.search!.trim()
    }
    if (filters.gender) params.gender = filters.gender
    if (filters.status) {
      params.status = filters.status
    } else {
      params.includeInactive = true
    }
    if (filters.dateOfBirthFrom) params.dateOfBirthFrom = filters.dateOfBirthFrom
    if (filters.dateOfBirthTo) params.dateOfBirthTo = filters.dateOfBirthTo
    if (filters.hasAllergies !== undefined) params.hasAllergies = filters.hasAllergies
    if (filters.hasInsurance !== undefined) params.hasInsurance = filters.hasInsurance
    
    return params
  }, [filters])

  const {
    data: paginatedData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['patients', 'list', 'paged', searchParams, page, pageSize, sortBy, sortDirection],
    queryFn: async () => {
      if (!searchParams) {
        throw new Error('Search params required')
      }
      return await patientService.getAll({
        ...searchParams,
        page,
        pageSize,
        sortBy,
        sortDirection,
      })
    },
    enabled: searchParams !== null,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  })

  const patients = useMemo(() => {
    if (!paginatedData) return []
    return extractPatients(paginatedData)
  }, [paginatedData])

  const patientCount = patients ? patients.length : 0
  const totalCount = useMemo(() => {
    if (paginatedData && typeof paginatedData === 'object' && 'totalCount' in paginatedData) {
      return paginatedData.totalCount as number
    }
    return patientCount
  }, [paginatedData, patientCount])

  const totalPages = useMemo(() => {
    if (paginatedData && typeof paginatedData === 'object' && 'totalPages' in paginatedData) {
      return paginatedData.totalPages as number
    }
    return pageSize ? Math.ceil(totalCount / pageSize) : 1
  }, [paginatedData, pageSize, totalCount])

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages)
    }
  }, [page, totalPages])

  const markInactiveMutation = useMutation({
    mutationFn: (id: string) => patientService.markInactive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      setSelectedPatients([])
    },
  })

  const activateMutation = useMutation({
    mutationFn: (id: string) => patientService.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      setSelectedPatients([])
    },
  })

  const bulkMarkInactiveMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => patientService.markInactive(id)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      setSelectedPatients([])
    },
  })

  const filteredPatients = patients

  const handleMarkInactive = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to mark ${name} as inactive? This can be reversed.`)) {
      try {
        await markInactiveMutation.mutateAsync(id)
      } catch (error) {
        alert('Failed to mark patient as inactive')
      }
    }
  }

  const handleActivate = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to activate ${name}?`)) {
      try {
        await activateMutation.mutateAsync(id)
      } catch (error) {
        alert('Failed to activate patient')
      }
    }
  }

  const handleSelectPatient = (id: string) => {
    setSelectedPatients((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    )
  }

  const handleSelectAll = () => {
    if (selectedPatients.length === filteredPatients.length) {
      setSelectedPatients([])
    } else {
      setSelectedPatients(filteredPatients.map((p) => p.id))
    }
  }

  const handleBulkExport = (patients: Patient[]) => {
    const format = window.prompt('Export format: CSV or JSON?', 'CSV')
    if (format?.toUpperCase() === 'JSON') {
      exportPatientsToJSON(patients)
    } else {
      exportPatientsToCSV(patients)
    }
  }

  const handleSearch = () => {
    const params = buildSearchParams
    if (params) {
      setSearchParams(params)
      setHasSearched(true)
      setPage(1)
    } else {
      // No search criteria, clear results
      setSearchParams(null)
      setHasSearched(false)
      setPage(1)
    }
  }

  const handleClearFilters = () => {
    setFilters({ search: '' })
    setSearchParams(null)
    setHasSearched(false)
    setPage(1)
  }

  const handlePageSizeChange = (nextPageSize: number) => {
    if (nextPageSize === pageSize) return
    setPageSize(nextPageSize)
    setPage(1)
  }

  const handleSort = (column: 'patientNumber' | 'name' | 'dateOfBirth') => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortDirection('asc')
    }
    setPage(1)
  }

  const sortIndicator = (column: 'patientNumber' | 'name' | 'dateOfBirth') => {
    if (sortBy !== column) return '↕'
    return sortDirection === 'asc' ? '↑' : '↓'
  }

  const pageNumbers = useMemo(() => {
    if (totalPages <= 1) return [1]
    const maxButtons = 7
    const half = Math.floor(maxButtons / 2)
    let start = Math.max(1, page - half)
    let end = Math.min(totalPages, start + maxButtons - 1)
    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1)
    }
    const pages = []
    for (let i = start; i <= end; i += 1) {
      pages.push(i)
    }
    return pages
  }, [page, totalPages])

  const pageSummary = useMemo(() => {
    if (!hasSearched) return ''
    if (totalCount === 0) return 'No results'
    const startIndex = (page - 1) * pageSize + 1
    const endIndex = Math.min(totalCount, page * pageSize)
    return `${startIndex}-${endIndex} of ${totalCount}`
  }, [hasSearched, page, pageSize, totalCount])

  if (isLoading) return <div className="loading" role="status" aria-live="polite">{t('patients.loadingPatients')}</div>
  if (error) return <div className="error" role="alert" aria-live="assertive">{t('patients.errorLoadingPatients')}</div>

  return (
    <div className="patients">
      <div className="patients-header">
        <div>
          <h1>{t('patients.title')}</h1>
          <p className="patients-count" aria-live="polite">
            {t('patients.found', { count: totalCount })}
          </p>
        </div>
        <div className="header-actions">
          <div className="view-mode-toggle">
            <button
              className={viewMode === 'table' ? 'active' : ''}
              onClick={() => setViewMode('table')}
              title="Table view"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M3 3h14v14H3V3zm1 1v12h12V4H4zm1 1h10v2H5V5zm0 3h10v2H5V8zm0 3h10v2H5v-2z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M3 3h6v6H3V3zm8 0h6v6h-6V3zM3 11h6v6H3v-6zm8 0h6v6h-6v-6z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
          <button
            className="btn-secondary"
            onClick={() => handleBulkExport(filteredPatients)}
            disabled={filteredPatients.length === 0}
          >
            Export All
          </button>
          <button
            className="btn-primary"
            onClick={() => navigate('/patients/new')}
            aria-label={t('patients.addNew')}
          >
            {t('patients.addNew')}
          </button>
        </div>
      </div>

      <div className="patients-filters-wrapper">
        <PatientSearchFilters
          filters={filters}
          onFiltersChange={setFilters}
          onClear={handleClearFilters}
          onSearch={handleSearch}
          isLoading={isLoading}
          hasSearched={hasSearched}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
        />
        {hasSearched && totalPages > 1 && (
          <div className="patients-pagination">
            <div className="page-summary" aria-live="polite">
              {pageSummary}
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              First
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <div className="page-number-group" role="navigation" aria-label="Patient list pages">
              {pageNumbers[0] > 1 && (
                <>
                  <button
                    type="button"
                    className={`page-number ${page === 1 ? 'active' : ''}`}
                    onClick={() => setPage(1)}
                  >
                    1
                  </button>
                  {pageNumbers[0] > 2 && <span className="page-ellipsis">…</span>}
                </>
              )}
              {pageNumbers.map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  className={`page-number ${pageNumber === page ? 'active' : ''}`}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
              {pageNumbers[pageNumbers.length - 1] < totalPages && (
                <>
                  {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                    <span className="page-ellipsis">…</span>
                  )}
                  <button
                    type="button"
                    className={`page-number ${page === totalPages ? 'active' : ''}`}
                    onClick={() => setPage(totalPages)}
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
            >
              Next
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              Last
            </button>
          </div>
        )}
      </div>

      {selectedPatients.length > 0 && (
        <PatientBulkActions
          selectedPatients={selectedPatients}
          onBulkMarkInactive={bulkMarkInactiveMutation.mutateAsync}
          onBulkExport={handleBulkExport}
          patients={filteredPatients}
        />
      )}

      {viewMode === 'table' ? (
        <div className="patients-table-container">
          <table className="patients-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={
                      filteredPatients.length > 0 &&
                      selectedPatients.length === filteredPatients.length
                    }
                    onChange={handleSelectAll}
                  />
                </th>
                <th>
                  <button
                    type="button"
                    className="sort-button"
                    onClick={() => handleSort('patientNumber')}
                  >
                    Patient Number <span className="sort-indicator">{sortIndicator('patientNumber')}</span>
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="sort-button"
                    onClick={() => handleSort('name')}
                  >
                    Name <span className="sort-indicator">{sortIndicator('name')}</span>
                  </button>
                </th>
                <th>Email</th>
                <th>Phone</th>
                <th>
                  <button
                    type="button"
                    className="sort-button"
                    onClick={() => handleSort('dateOfBirth')}
                  >
                    Date of Birth <span className="sort-indicator">{sortIndicator('dateOfBirth')}</span>
                  </button>
                </th>
                <th>Gender</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    {hasSearched
                      ? t('patients.noPatientsMatching')
                      : 'Enter search criteria and click Search to find patients'}
                  </td>
                </tr>
              ) : (
                filteredPatients.map((patient) => {
                  const isSecondaryRecord = patient.isPrimary === false || !!patient.primaryPatientId
                  return (
                    <tr key={patient.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedPatients.includes(patient.id)}
                          onChange={() => handleSelectPatient(patient.id)}
                        />
                      </td>
                      <td>{patient.patientNumber || patient.id.slice(0, 8)}</td>
                      <td>
                        <div className="patient-result-name">
                          {`${patient.firstName} ${patient.lastName}`}
                          {isSecondaryRecord && (
                            <span
                              className="linked-status-badge secondary"
                              title="Secondary record - read-only"
                            >
                              Secondary (read-only)
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{patient.email || '-'}</td>
                      <td>{patient.phone}</td>
                      <td>{formatDate(patient.dateOfBirth)}</td>
                      <td>{patient.gender}</td>
                      <td>
                        <div className="action-buttons">
                          <Link to={`/patients/${patient.id}`} className="btn-link">
                            View
                          </Link>
                          {isSecondaryRecord ? (
                            <span className="btn-link" title="Secondary records are read-only">
                              Edit
                            </span>
                          ) : (
                            <Link to={`/patients/${patient.id}/edit`} className="btn-link">
                              Edit
                            </Link>
                          )}
                          {patient.isActive ? (
                            <button
                              onClick={() =>
                                handleMarkInactive(patient.id, `${patient.firstName} ${patient.lastName}`)
                              }
                              className="btn-archive"
                              disabled={markInactiveMutation.isPending || isSecondaryRecord}
                              title={isSecondaryRecord ? 'Secondary records are read-only' : undefined}
                            >
                              {markInactiveMutation.isPending ? 'Marking Inactive...' : 'Mark Inactive'}
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                handleActivate(patient.id, `${patient.firstName} ${patient.lastName}`)
                              }
                              className="btn-primary"
                              disabled={activateMutation.isPending || isSecondaryRecord}
                              title={isSecondaryRecord ? 'Secondary records are read-only' : undefined}
                            >
                              {activateMutation.isPending ? 'Activating...' : 'Activate'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="patients-grid">
          {filteredPatients.length === 0 ? (
            <div className="empty-state">
              {hasSearched
                ? 'No patients found matching your filters'
                : 'Enter search criteria and click Search to find patients'}
            </div>
          ) : (
            filteredPatients.map((patient) => {
              const isSecondaryRecord = patient.isPrimary === false || !!patient.primaryPatientId
              return (
                <div key={patient.id} className="patient-card">
                  <div className="patient-card-header">
                    <input
                      type="checkbox"
                      checked={selectedPatients.includes(patient.id)}
                      onChange={() => handleSelectPatient(patient.id)}
                    />
                    <span className="patient-number">
                      {patient.patientNumber || patient.id.slice(0, 8)}
                    </span>
                  </div>
                  <div className="patient-card-body">
                    <h3>
                      {`${patient.firstName} ${patient.lastName}`}
                      {isSecondaryRecord && (
                        <span
                          className="linked-status-badge secondary"
                          title="Secondary record - read-only"
                        >
                          Secondary (read-only)
                        </span>
                      )}
                    </h3>
                    <div className="patient-card-info">
                      <p>
                        <strong>DOB:</strong> {new Date(patient.dateOfBirth).toLocaleDateString()}
                      </p>
                      <p>
                        <strong>Gender:</strong> {patient.gender}
                      </p>
                      {patient.email && (
                        <p>
                          <strong>Email:</strong> {patient.email}
                        </p>
                      )}
                      <p>
                        <strong>Phone:</strong> {patient.phone}
                      </p>
                    </div>
                  </div>
                  <div className="patient-card-actions">
                    <Link to={`/patients/${patient.id}`} className="btn-link">
                      View
                    </Link>
                    {isSecondaryRecord ? (
                      <span className="btn-link" title="Secondary records are read-only">
                        Edit
                      </span>
                    ) : (
                      <Link to={`/patients/${patient.id}/edit`} className="btn-link">
                        Edit
                      </Link>
                    )}
                    {patient.isActive ? (
                      <button
                        onClick={() =>
                          handleMarkInactive(patient.id, `${patient.firstName} ${patient.lastName}`)
                        }
                        className="btn-archive"
                        disabled={markInactiveMutation.isPending || isSecondaryRecord}
                        title={isSecondaryRecord ? 'Secondary records are read-only' : undefined}
                      >
                        {markInactiveMutation.isPending ? 'Marking Inactive...' : 'Mark Inactive'}
                      </button>
                    ) : (
                      <button
                        onClick={() =>
                          handleActivate(patient.id, `${patient.firstName} ${patient.lastName}`)
                        }
                        className="btn-primary"
                        disabled={activateMutation.isPending || isSecondaryRecord}
                        title={isSecondaryRecord ? 'Secondary records are read-only' : undefined}
                      >
                        {activateMutation.isPending ? 'Activating...' : 'Activate'}
                      </button>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}

